import { defineStore } from "pinia";
import { computed, nextTick, ref } from "vue";
import { scrollToTop } from "@polargold/pg-frontend-core";
import { useApi } from "@polargold/pg-frontend-vue";
import { apiLibrary } from "@/api";
import * as Sentry from "@sentry/vue";
import {
    useProductSearchPayloadStore
} from "@/stores/productSearch/productSearchPayloadStore";
import {
    useProductSearchIsLoadingStore
} from "@/stores/productSearch/productSearchIsLoadingStore";
import type {
    ProductSearchResponse
} from "@/api/modules/autoQ/productSearch/searchResultTypes.ts";
import type { Nullable } from "@/types";
import {
    useProductSearchPaginationStore
} from "@/stores/productSearch/productSearchPaginationStore";
import {
    useProductSearchFilterOptionsStore
} from "@/stores/productSearch/productSearchFilterOptionsStore";
import { extractSearchResultItems } from "@polargold/pg-frontend-core";

export const useProductSearchResultsStore = defineStore("productSearchResultsStore", () => {
    const results = ref<Nullable<ProductSearchResponse>>(null);

    const payloadStore = useProductSearchPayloadStore();
    const isLoadingStore = useProductSearchIsLoadingStore();
    const paginationStore = useProductSearchPaginationStore();
    const filterOptionsStore = useProductSearchFilterOptionsStore();

    // Loading state is tracked on the separate, shared productSearchIsLoadingStore (other
    // stores/components read it too), not useApi's own isLoading - set/reset explicitly around
    // callApi() instead.
    const { hasError, callApi } = useApi({
        callback: async () => {
            const { data } = await apiLibrary.autoQ.productSearch.search(payloadStore.payload);

            return data;
        },
        onSuccess: (data) => {
            results.value = data ?? null;

            if (data) {
                paginationStore.setTotalCount(data.hits.total.value);
                filterOptionsStore.setFilterOptions(data.aggregations);
            }

            scrollToTop();
        },
        onError: (err) => Sentry.captureException(err),
        rethrow: false,
    });

    const fetchResults = async () => {
        await isLoadingStore.setLoadingState();

        await callApi();

        await isLoadingStore.resetLoadingState();
    };
    const reset = async () => {
        results.value = null;

        await nextTick();

        return Promise.resolve();
    };

    const searchResultItems = computed(() => extractSearchResultItems(results.value));
    const hasResults = computed(() => searchResultItems.value.length > 0);

    return {
        results,
        fetchResults,
        reset,
        hasError,
        hasResults,
        searchResultItems,
    };
});